import { getAccessToken, getScopeHeaders } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_WAREHOUSEEXEC_API ?? '/api/v1';

export type FulfilmentJob = {
  id: string;
  order_id: string;
  warehouse_id: string;
  status: string;
  priority: number;
  pick_strategy: string;
  wave_id?: string;
  worker_id?: string;
  created_at: string;
  updated_at: string;
};

export type PickTask = {
  id: string;
  job_id: string;
  wave_id?: string;
  warehouse_id: string;
  bin_id?: string;
  zone_id?: string;
  status: string;
  worker_id?: string;
  priority: number;
  route_seq: number;
  created_at: string;
};

export type PickingWave = {
  id: string;
  wave_code: string;
  warehouse_id: string;
  strategy: string;
  status: string;
  task_count: number;
  created_at: string;
};

export type PackingSession = {
  id: string;
  job_id: string;
  warehouse_id: string;
  status: string;
  worker_id?: string;
  created_at: string;
};

export type Package = {
  id: string;
  session_id: string;
  job_id: string;
  carton_type: string;
  weight: number;
  length: number;
  width: number;
  height: number;
  barcode?: string;
  tracking_number?: string;
};

export type DispatchBatch = {
  id: string;
  warehouse_id: string;
  status: string;
  carrier?: string;
  dock_id?: string;
  manifest_id?: string;
  package_count: number;
  created_at: string;
};

export type WarehouseDashboard = {
  active_jobs: number;
  pending_picks: number;
  active_packs: number;
  dispatch_queue: number;
  workers_active: number;
  avg_pick_time_min: number;
  packing_accuracy: number;
};

export type WarehouseKPI = {
  warehouse_id: string;
  picks_completed: number;
  packs_completed: number;
  dispatches_completed: number;
  avg_pick_time_min: number;
  avg_pack_time_min: number;
  packing_accuracy: number;
  pick_accuracy: number;
  productivity_score: number;
};

export type WarehouseWorker = {
  id: string;
  code: string;
  name: string;
  warehouse_id: string;
  active: boolean;
  productivity: number;
};

export type Dock = {
  id: string;
  warehouse_id: string;
  code: string;
  status: string;
  carrier?: string;
};

export type WarehouseIntelligence = {
  warehouse_id: string;
  congestion_level: number;
  recommended_workers: number;
  pick_time_prediction_min: number;
  pack_time_prediction_min: number;
  dispatch_priority_score: number;
  carton_recommendation?: string;
  pick_route_hint?: string;
};

export type PickLine = {
  product_id: string;
  sku?: string;
  quantity: number;
  bin_id?: string;
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...getScopeHeaders(),
      ...init?.headers,
    },
  });
  if (!res.ok) throw new Error(await res.text() || res.statusText);
  if (res.status === 204) return undefined as T;
  const json = await res.json();
  return (json.data ?? json) as T;
}

export const warehouseexecApi = {
  dashboard: (warehouseId = 'wh-main') =>
    request<WarehouseDashboard>(`/fulfilment/dashboard?warehouse_id=${encodeURIComponent(warehouseId)}`),

  listJobs: (warehouseId = 'wh-main') =>
    request<FulfilmentJob[]>(`/fulfilment/jobs?warehouse_id=${encodeURIComponent(warehouseId)}`),

  createJob: (body: { order_id: string; warehouse_id: string; pick_strategy?: string; items: PickLine[] }) =>
    request<FulfilmentJob>('/fulfilment/jobs', { method: 'POST', body: JSON.stringify(body) }),

  listPickTasks: (warehouseId = 'wh-main') =>
    request<PickTask[]>(`/picking/tasks?warehouse_id=${encodeURIComponent(warehouseId)}`),

  createWave: (body: { warehouse_id: string; strategy: string; job_ids: string[] }) =>
    request<PickingWave>('/picking/waves', { method: 'POST', body: JSON.stringify(body) }),

  confirmPick: (body: { task_id: string; worker_id: string; barcode?: string; items: { item_id: string; picked_qty: number }[] }) =>
    request<PickTask>('/picking/confirm', { method: 'POST', body: JSON.stringify(body) }),

  startPacking: (body: { job_id: string; worker_id: string }) =>
    request<PackingSession>('/packing/start', { method: 'POST', body: JSON.stringify(body) }),

  completePacking: (body: { session_id: string; weight: number; length: number; width: number; height: number }) =>
    request<Package>('/packing/complete', { method: 'POST', body: JSON.stringify(body) }),

  createDispatch: (body: { warehouse_id: string; carrier: string; job_ids: string[]; dock_id?: string }) =>
    request<DispatchBatch>('/dispatch/create', { method: 'POST', body: JSON.stringify(body) }),

  completeDispatch: (batchId: string) =>
    request<DispatchBatch>('/dispatch/complete', { method: 'POST', body: JSON.stringify({ batch_id: batchId }) }),

  kpis: (warehouseId = 'wh-main') =>
    request<WarehouseKPI>(`/warehouse/kpis?warehouse_id=${encodeURIComponent(warehouseId)}`),

  workers: (warehouseId = 'wh-main') =>
    request<WarehouseWorker[]>(`/warehouse/workers?warehouse_id=${encodeURIComponent(warehouseId)}`),

  docks: (warehouseId = 'wh-main') =>
    request<Dock[]>(`/warehouse/docks?warehouse_id=${encodeURIComponent(warehouseId)}`),

  intelligence: (warehouseId = 'wh-main') =>
    request<WarehouseIntelligence>(`/warehouse/intelligence?warehouse_id=${encodeURIComponent(warehouseId)}`),
};
